import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Download, Search, CalendarClock } from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { CopyText } from '@/components/ui/CopyText';

interface Row {
  id: string; orderNo: string; orderType: 'B2C' | 'B2B';
  awbNo?: string; lrNo?: string;
  statusId: number; statusName: string;
  edd?: string; bookingDate?: string; totalAttempts?: number; deliveryType?: string;
  customerName?: string; customerMobile?: string; customerCity?: string; customerState?: string; customerPincode?: string;
  invoiceValue?: string; codValue?: string;
  merchantName?: string | null; courierName?: string | null; daysOverdue: number;
}
interface ListResp { total: number; page: number; pageSize: number; items: Row[] }

type EddSummary = {
  total: number;
  oldestDays: number;
  buckets: Array<{ key: string; label: string; count: number; pct: number }>;
};

export function EddExpiredPage() {
  const [orderType, setOrderType] = useState<'B2C' | 'B2B'>('B2C');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const { data, isLoading } = useQuery({
    queryKey: ['edd-expired', { orderType, q, page }],
    queryFn: async () => {
      const params: any = { orderType, page, pageSize };
      if (q) params.search = q;
      return (await api.get<ListResp>('/edd-expired', { params })).data;
    },
  });

  // Ageing bands, from the same filters as the list so the tiles and the rows can never disagree.
  const { data: summary } = useQuery({
    queryKey: ['edd-expired-summary', { orderType, q }],
    queryFn: async () => {
      const params: any = { orderType };
      if (q) params.search = q;
      return (await api.get<EddSummary>('/edd-expired/summary', { params })).data;
    },
  });

  function runSearch() { setQ(search.trim()); setPage(1); }

  function exportCsv() {
    const qs = new URLSearchParams();
    qs.set('orderType', orderType);
    if (q) qs.set('search', q);
    downloadBlob(`/edd-expired/export?${qs.toString()}`, `edd-expired-${orderType}-${new Date().toISOString().slice(0, 10)}.csv`);
  }

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><CalendarClock size={22} className="text-rose-500" /> EDD Expired</h1>
          <p className="text-sm text-slate-500">Undelivered orders whose Expected Delivery Date has already passed</p>
        </div>
        <button onClick={exportCsv} className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-slate-50">
          <Download size={14} /> Export
        </button>
      </div>

      {/* How overdue the backlog is. A flat "7,817 expired" is not actionable — a parcel a day past
          its EDD is normal slippage, one a month past it is a lost shipment nobody has chased. The
          bands do not overlap and sum to the total, so this reads as a distribution. */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        {(summary?.buckets ?? []).map((b, i) => {
          // Deepening red with age: the last two bands are the ones worth acting on today.
          const tone = ['bg-slate-50 border-slate-200 text-slate-700',
                        'bg-amber-50 border-amber-200 text-amber-800',
                        'bg-amber-50 border-amber-300 text-amber-900',
                        'bg-orange-50 border-orange-200 text-orange-800',
                        'bg-rose-50 border-rose-200 text-rose-700',
                        'bg-rose-100 border-rose-300 text-rose-800'][i] ?? 'bg-slate-50 border-slate-200';
          return (
            <div key={b.key} className={`rounded-lg border p-3 ${tone}`}>
              <div className="text-[11px] uppercase tracking-wide opacity-80">{b.label}</div>
              <div className="text-2xl font-bold mt-0.5">{b.count.toLocaleString()}</div>
              <div className="text-[11px] opacity-70">{b.pct}% of expired</div>
            </div>
          );
        })}
      </div>
      {summary && (
        <div className="text-xs text-slate-500 mb-4">
          <b>{summary.total.toLocaleString()}</b> shipments past their EDD
          {summary.oldestDays > 0 && <> · oldest is <b>{summary.oldestDays}</b> days overdue</>}
        </div>
      )}

      {/* B2C / B2B tabs */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 mb-4 w-fit">
        {(['B2C', 'B2B'] as const).map(t => (
          <button key={t} onClick={() => { setOrderType(t); setPage(1); }}
            className={`px-5 py-1.5 text-sm rounded-md transition ${orderType === t ? 'bg-white shadow-sm text-primary font-medium' : 'text-slate-600 hover:text-slate-900'}`}>
            {t}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-3 mb-4 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
            placeholder="Search order no / AWB / LR / customer / mobile"
            className="input w-full pl-9"
          />
        </div>
        <button onClick={runSearch} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium">Search</button>
        {q && <button onClick={() => { setSearch(''); setQ(''); setPage(1); }} className="px-3 py-2 border border-slate-200 rounded-lg text-xs">Clear</button>}
        <div className="ml-auto text-xs text-slate-500">{total} order{total === 1 ? '' : 's'} overdue</div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-3 py-2.5">Order No</th>
              <th className="text-left px-3 py-2.5">AWB / LR</th>
              <th className="text-left px-3 py-2.5">Merchant</th>
              <th className="text-left px-3 py-2.5">Courier</th>
              <th className="text-left px-3 py-2.5">Customer</th>
              <th className="text-left px-3 py-2.5">EDD</th>
              <th className="text-right px-3 py-2.5">Days Overdue</th>
              <th className="text-left px-3 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (<tr><td colSpan={8} className="text-center py-10"><Loader2 size={20} className="mx-auto animate-spin text-primary" /></td></tr>)}
            {!isLoading && items.length === 0 && (<tr><td colSpan={8} className="text-center py-12 text-slate-400">No overdue orders for these filters.</td></tr>)}
            {items.map(r => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                <td className="px-3 py-2 text-xs font-medium">{r.orderNo || '—'}</td>
                <td className="px-3 py-2 text-xs font-mono">
                  <CopyText value={r.awbNo || r.lrNo || ''} display={<span>{r.awbNo || r.lrNo || '—'}</span>} iconSize={10} silent />
                </td>
                <td className="px-3 py-2 text-xs text-slate-700">{r.merchantName || '—'}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{r.courierName || '—'}</td>
                <td className="px-3 py-2 text-xs">
                  <div className="font-medium text-slate-700">{r.customerName || '—'}</div>
                  <div className="text-slate-500">{[r.customerCity, r.customerState].filter(Boolean).join(', ')}</div>
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">{r.edd ? new Date(r.edd).toLocaleDateString('en-IN') : '—'}</td>
                <td className="px-3 py-2 text-right">
                  <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-rose-100 text-rose-700">{r.daysOverdue}d</span>
                </td>
                <td className="px-3 py-2 text-xs">{r.statusName}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 text-xs text-slate-600">
          <span>Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-40">Prev</button>
            <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} className="px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-40">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
